import { ApolloError } from '@apollo/client';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  useStyleConfig,
  VStack,
} from '@chakra-ui/react';
import ArrowForwardSharpIcon from '@gamma/ui/icons/ArrowForwardSharpIcon';
import InfoSharpIcon from '@gamma/ui/icons/InfoSharpIcon';
import { AccordionPanel } from '@gamma/ui/layout';
import { Popover } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { SingleValue } from 'chakra-react-select';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ROUTES,
  MISSING_DATA_PLACEHOLDER,
} from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers/AuthProvider/AuthContext';
import {
  CrowdStrikeEndpointDevice,
  MicrosoftDefenderEndpointDevice,
  useGetEDREndpointDevices,
} from '../../../../../entities/queries';
import { hasAnyEDRIntegrationEnabledForTenant } from '../../../../../integrations/pages';
import { EDRIntegration } from '../../../../../integrations/pages/IntegrationsConstants';
import { SantizedDetection } from '../../../../pages';
import {
  AlertEntity,
  Detection,
  ExcludedEntity,
} from '@investigator/shared/generated/graphql';
import { CrowdstrikeDetectionDataPanel } from '../../CrowdstrikeDetectionDataPanel';
import { MSDefenderDetectionDataPanel } from '../../MSDefenderDetectionDataPanel';
import { DetectionDrawerEntityHeader } from './Components';

const { entityEDR } = i18n.pages.detections;

interface EntityOption {
  value: string;
  label: string;
  type: string;
}

// Local entity state for drawer-specific entity selection
interface LocalEntityState {
  entityId?: string;
  entityName?: string;
  entityType?: string;
}

const { systemSettingsIntegrationsConfig } = ROUTES;
const { entity } = i18n.pages.entityDetails.excludeEntity;

export interface EntityPanelRowProps {
  heading: string;
  children?: ReactNode;
  disableBorders?: boolean;
  grayHeader?: boolean;
  'data-testid'?: string;
  hasTestIdLabel?: boolean;
  hasTestIdValue?: boolean;
}

export const EntityPanelRow = ({
  heading,
  children,
  disableBorders,
  grayHeader = true,
  'data-testid': dataTestId,
  hasTestIdLabel = true,
  hasTestIdValue = true,
}: EntityPanelRowProps) => {
  const { color } = useStyleConfig('InfoHeaders');
  return (
    <Flex alignItems="center" w="100%">
      <Box w="120px" mr={2} color={grayHeader ? String(color) : 'text.300'}>
        <Box
          as="span"
          color={grayHeader ? 'gray.200' : 'text.300'}
          data-testid={
            dataTestId && hasTestIdLabel ? `${dataTestId}-label` : undefined
          }
          textStyle="body-md"
        >
          {heading}
        </Box>
      </Box>
      <Box
        w="225px"
        flexGrow={1}
        wordBreak={disableBorders ? 'break-all' : 'normal'}
      >
        <Box
          as="span"
          data-testid={
            dataTestId && hasTestIdValue ? `${dataTestId}-value` : undefined
          }
          textStyle="body-md"
        >
          {children ? children : MISSING_DATA_PLACEHOLDER}
        </Box>
      </Box>
    </Flex>
  );
};

export interface IDetectionDrawerEntityEDRPanelProps {
  isRelated?: boolean;
  refetchMain?: boolean;
  detectionInfo?: Detection | SantizedDetection;
  excludeEntitiesData?: ExcludedEntity[];
  excludeEntitiesError?: ApolloError;
  sourceEntities?: AlertEntity[];
  totalSourceEntities?: number;
  destinationEntities?: AlertEntity[];
  totalDestinationEntities?: number;
  disableBorders?: boolean;
}

export const DetectionDrawerEntityEDRPanel = ({
  isRelated,
  detectionInfo,
  sourceEntities,
  totalSourceEntities,
  destinationEntities,
  totalDestinationEntities,
  disableBorders = false,
}: IDetectionDrawerEntityEDRPanelProps) => {
  const { tenant = '' } = detectionInfo || {};
  const { userPrivileges } = useContext(AuthContext);
  const tenantPrivileges = userPrivileges?.[tenant];
  const hasAnyEDRIntegrationEnabled = hasAnyEDRIntegrationEnabledForTenant(
    tenant,
    userPrivileges || {},
  );

  // Using local state
  // this is only used for the detection drawer to fetch EDR data by entity type tab selection
  const [activeEntity, setActiveEntity] = useState<
    LocalEntityState | undefined
  >();
  const [activeEntityType, setActiveEntityType] = useState<string>('source');
  const [selectedSrcOption, setSelectedSrcOption] =
    useState<EntityOption | null>(null);
  const [selectedDestOption, setSelectedDestOption] =
    useState<EntityOption | null>(null);
  const navigate = useNavigate();

  const sourceOptions: EntityOption[] = sourceEntities?.length
    ? sourceEntities.map((entity) => ({
        value: entity?.entity_id ?? '',
        label: entity?.entity_name ?? '',
        type: entity?.entity_type ?? '',
      }))
    : [];

  const destinationOptions: EntityOption[] = destinationEntities?.length
    ? destinationEntities.map((entity) => ({
        value: entity?.entity_id ?? '',
        label: entity?.entity_name ?? '',
        type: entity?.entity_type ?? '',
      }))
    : [];

  // Helper to convert EntityOption to EntityInstance
  const toEntityInstance = (option: EntityOption | null) => {
    if (!option) return undefined;
    return {
      entityId: option.value,
      entityName: option.label,
      entityType: option.type,
    };
  };

  useEffect(() => {
    setSelectedSrcOption({
      value: sourceEntities?.[0]?.entity_id || '',
      label: sourceEntities?.[0]?.entity_name || '',
      type: sourceEntities?.[0]?.entity_type || '',
    });
  }, [sourceEntities]);

  useEffect(() => {
    setSelectedDestOption({
      value: destinationEntities?.[0]?.entity_id || '',
      label: destinationEntities?.[0]?.entity_name || '',
      type: destinationEntities?.[0]?.entity_type || '',
    });
  }, [destinationEntities]);

  const handleSrcEntityChange = (event: SingleValue<EntityOption>) => {
    setSelectedSrcOption(event);
    setActiveEntity(toEntityInstance(event));
  };

  const handleDestEntityChange = (event: SingleValue<EntityOption>) => {
    setSelectedDestOption(event);
    setActiveEntity(toEntityInstance(event));
  };

  useEffect(() => {
    if (activeEntityType === 'source') {
      setActiveEntity(toEntityInstance(selectedSrcOption));
    } else if (activeEntityType === 'destination') {
      setActiveEntity(toEntityInstance(selectedDestOption));
    }
  }, [
    activeEntityType,
    selectedSrcOption,
    selectedDestOption,
    setActiveEntity,
  ]);

  const entityName =
    activeEntity?.entityName || detectionInfo?.alert_entity?.entity_name || '';
  const entityType =
    activeEntity?.entityType || detectionInfo?.alert_entity?.entity_type || '';
  const {
    data: EDRData,
    loading,
    error,
  } = useGetEDREndpointDevices({
    skip: !tenant || !hasAnyEDRIntegrationEnabled || !entityName || !entityType,
    variables: {
      edr_endpoint_device_inputs: [
        {
          as_of: detectionInfo?.latest_start_timestamp,
          tenant,
          alert_entity_info: {
            entity_name: entityName,
            entity_type: entityType,
          },
        },
      ],
    },
  });
  const organizedData = useMemo(() => {
    if (!EDRData?.getEdrEndpointDevices) return null;
    const processedEDRData: Partial<
      Record<
        EDRIntegration,
        CrowdStrikeEndpointDevice | MicrosoftDefenderEndpointDevice
      >
    > = {};
    EDRData.getEdrEndpointDevices.edr_endpoint_devices.forEach(
      (integration) => {
        processedEDRData[integration.data_source] = integration;
      },
    );
    return processedEDRData;
  }, [EDRData]);
  const { isOrgTenant } = useContext(AuthContext);
  const EntityTitle = (
    <HStack alignItems="center">
      <Box as="span">{entity}</Box>
      {!hasAnyEDRIntegrationEnabled && (
        <Box h="20px">
          <Popover
            isLazy
            width="320px"
            trigger="hover"
            placement="right"
            title={entityEDR.entityEnrichment}
            body={
              isOrgTenant
                ? entityEDR.getEntityEnrichmentOrg
                : entityEDR.getEntityEnrichment
            }
            footer={
              !isOrgTenant && (
                <Button
                  size="md"
                  variant="solid"
                  colorScheme="blue"
                  data-testid="system-settings-integration-config-button"
                  rightIcon={<ArrowForwardSharpIcon />}
                  onClick={() => {
                    navigate({
                      pathname: systemSettingsIntegrationsConfig,
                    });
                  }}
                >
                  {entityEDR.integrationPage}
                </Button>
              )
            }
          >
            <InfoSharpIcon color="text.secondary" />
          </Popover>
        </Box>
      )}
    </HStack>
  );
  return loading || error ? (
    <GraphQLReqStatus loading={loading} error={error} />
  ) : (
    <Box w="100%" data-testid="detection-drawer-entity-edr-panel">
      <AccordionPanel
        w="100%"
        title={EntityTitle}
        layerStyle="first"
        reduceMotion={true}
        disableBorders={disableBorders}
        data-testid="entity-panel-heading"
        storageKey="detection-entity-panel"
      >
        <VStack spacing={2.5} alignItems="start">
          <DetectionDrawerEntityHeader
            selectedSrcOption={selectedSrcOption}
            selectedDestOption={selectedDestOption}
            activeEntityType={activeEntityType}
            setActiveEntityType={setActiveEntityType}
            sourceOptions={sourceOptions}
            destinationOptions={destinationOptions}
            totalSourceEntities={totalSourceEntities}
            totalDestinationEntities={totalDestinationEntities}
            handleSrcEntityChange={handleSrcEntityChange}
            handleDestEntityChange={handleDestEntityChange}
          />
          <EntityPanelRow heading={entityType ?? ''}>
            {detectionInfo?.alert_entity?.entity_type}
          </EntityPanelRow>
          {tenantPrivileges?.crowdstrike && organizedData?.crowdstrike && (
            <>
              <Divider />
              <CrowdstrikeDetectionDataPanel
                data={organizedData.crowdstrike as CrowdStrikeEndpointDevice}
                detectionInfo={detectionInfo as Detection}
                isRelated={isRelated}
                actionEnabled={tenantPrivileges['crowdstrike_action_enabled']}
              />
            </>
          )}
          {tenantPrivileges?.microsoft_defender &&
            organizedData?.microsoft_defender && (
              <>
                <Divider />
                <MSDefenderDetectionDataPanel
                  data={
                    organizedData.microsoft_defender as MicrosoftDefenderEndpointDevice
                  }
                  detectionInfo={detectionInfo as Detection}
                  isRelated={isRelated}
                  actionEnabled={
                    tenantPrivileges['microsoft_defender_action_enabled']
                  }
                />
              </>
            )}
        </VStack>
      </AccordionPanel>
    </Box>
  );
};
